import { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    useTheme,
    Alert,
    Card,
    CardHeader,
    IconButton,
    Tooltip,
    Chip,
    TablePagination,
    TextField,
    InputAdornment,
    Button,
    Stack,
    TableSortLabel,
    Paper
} from '@mui/material';
import { 
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { DateTime } from 'luxon';

const Clients = () => {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'lastName', direction: 'asc' });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [hasSearched, setHasSearched] = useState(false);
    const navigate = useNavigate();

    const fetchClients = async (pageNum = page + 1, limit = rowsPerPage) => {
        if (!searchQuery.trim()) {
            setClients([]);
            setTotalCount(0);
            setHasSearched(false);
            return;
        }

        try {
            setLoading(true);
            const response = await api.get('/admin/clients/db/search', {
                params: {
                    query: searchQuery,
                    page: pageNum,
                    limit
                }
            });
            setClients(response.data.clients || []);
            setTotalCount(response.data.pagination?.total || 0);
            setHasSearched(true);
        } catch (error) {
            setError('Failed to load clients');
        } finally {
            setLoading(false);
        }
    };

    const handleRequestSort = (property) => {
        const isAsc = sortConfig.key === property && sortConfig.direction === 'asc';
        setSortConfig({ key: property, direction: isAsc ? 'desc' : 'asc' });
    };

    const sortData = (data) => {
        return data.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (sortConfig.key === 'childrenCount') {
                aValue = a.childrenCount || 0;
                bValue = b.childrenCount || 0;
            }

            if (aValue === bValue) {
                return 0;
            }
            
            const comparison = aValue < bValue ? -1 : 1;
            return sortConfig.direction === 'desc' ? -comparison : comparison;
        });
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;
        setPage(0);
        fetchClients(1, rowsPerPage);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        setClients([]);
        setTotalCount(0);
        setPage(0);
        setHasSearched(false);
        setError(null);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        fetchClients(newPage + 1, rowsPerPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        fetchClients(1, newRowsPerPage);
    };

    // Get sorted clients
    const displayedClients = sortData([...clients]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_SHORT);
    };

    const SortableTableCell = ({ id, label, numeric = false }) => (
        <TableCell
            sortDirection={sortConfig.key === id ? sortConfig.direction : false}
            align={numeric ? 'right' : 'left'}
        >
            <TableSortLabel
                active={sortConfig.key === id}
                direction={sortConfig.key === id ? sortConfig.direction : 'asc'}
                onClick={() => handleRequestSort(id)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Clients
            </Typography>

            <Card sx={{ mb: 4 }}>
                <CardHeader
                    title="Client Search"
                    action={
                        <Stack direction="row" spacing={2}>
                            <Tooltip title="Refresh results">
                                <IconButton 
                                    onClick={() => fetchClients(page + 1, rowsPerPage)} 
                                    disabled={loading || !searchQuery.trim()}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    }
                />
                <Box sx={{ px: 3, pb: 3 }}>
                    <form onSubmit={handleSearch}>
                        <TextField
                            fullWidth
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by name or username..."
                            required
                            error={hasSearched && !searchQuery.trim()}
                            helperText={hasSearched && !searchQuery.trim() ? "Search query is required" : ""}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleClearSearch} edge="end">
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </form>
                </Box>
            </Card>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <SortableTableCell id="lastName" label="Name" />
                                <SortableTableCell id="ssUsername" label="Username" />
                                <SortableTableCell id="childrenCount" label="Children" numeric />
                                <SortableTableCell id="isCoachingOrPlaying" label="Coach/Player" />
                                <SortableTableCell id="active" label="Account" />
                                <SortableTableCell id="membershipStatus" label="Membership" />
                                <SortableTableCell id="lastSyncedAt" label="Last Synced" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : !hasSearched ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        Enter a search query to find clients
                                    </TableCell>
                                </TableRow>
                            ) : displayedClients.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No clients found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                displayedClients.map((client) => (
                                    <TableRow
                                        key={client.id}
                                        hover
                                        onClick={() => navigate(`/admin/clients/${client.id}`)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell>
                                            {client.firstName} {client.lastName}
                                        </TableCell>
                                        <TableCell>{client.ssUsername}</TableCell>
                                        <TableCell align="right">{client.childrenCount}</TableCell>
                                        <TableCell>
                                            {client.isCoachingOrPlaying ? (
                                                <Chip
                                                    label="Yes"
                                                    color="success"
                                                    size="small"
                                                />
                                            ) : (
                                                <Chip
                                                    label="No"
                                                    color="default"
                                                    size="small"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={client.active ? 'Active' : 'Inactive'}
                                                color={client.active ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={client.membershipStatus === 'active' ? 'Active' : 'Inactive'}
                                                color={client.membershipStatus === 'active' ? 'success' : 'error'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {client.lastSyncedAt ? formatDate(client.lastSyncedAt) : 'Never'}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {hasSearched && (
                    <TablePagination
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                )}
            </Paper>
        </Box>
    );
};

export default Clients; 